import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';

/**
 * Override SEO administrable (table seo_metadata), indexé sur le chemin de l'URL.
 * Monté globalement APRÈS les <Routes> : son Helmet est prioritaire sur celui des pages.
 * Ne rend RIEN si aucune entrée ou si les champs sont vides => SEO par défaut des pages préservé.
 */
const has = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

export default function SeoOverride() {
  const { pathname } = useLocation();
  const [o, setO] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    let active = true;
    setO(null);
    supabase
      .from('seo_metadata')
      .select('*')
      .eq('page_path', pathname)
      .maybeSingle()
      .then(({ data }) => { if (active && data) setO(data as Record<string, string>); })
      .then(undefined, () => { /* table absente / offline => fallback silencieux */ });
    return () => { active = false; };
  }, [pathname]);

  if (!o) return null;

  return (
    <Helmet>
      {has(o.meta_title) && <title>{o.meta_title}</title>}
      {has(o.meta_description) && <meta name="description" content={o.meta_description} />}
      {has(o.meta_keywords) && <meta name="keywords" content={o.meta_keywords} />}
      {has(o.robots) && <meta name="robots" content={o.robots} />}
      {has(o.og_title) && <meta property="og:title" content={o.og_title} />}
      {has(o.og_description) && <meta property="og:description" content={o.og_description} />}
      {has(o.og_image) && <meta property="og:image" content={o.og_image} />}
      {has(o.og_type) && <meta property="og:type" content={o.og_type} />}
      {has(o.twitter_card) && <meta property="twitter:card" content={o.twitter_card} />}
      {has(o.og_image) && <meta property="twitter:image" content={o.og_image} />}
      {has(o.canonical_url) && <link rel="canonical" href={o.canonical_url} />}
    </Helmet>
  );
}
